import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import BillingBackButton from "./BillingBackButton";
import checkCircleIcon from "../../../assets/images/icons/billing/check-circle.svg";
import { getTaxProfile, upsertTaxProfile, getCurrencyConfig, updateCurrency } from "../../../hook/useAdsApi";

// Wired to two separate endpoints behind one Save action: POST /ad-account/tax-profile
// (business_name -> billing_name, tax_country, billing_address, tax_id) and
// PATCH /ad-account/currency (currency_code). Both require a billing profile to already exist
// (upsertTaxProfile throws "Configure billing before adding tax information" otherwise) - the
// Dashboard already disables the "Tax and Billing" CTA until then, so this only gets reached
// with a real profile in the normal flow.
export default function AdTaxBillingInfo() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [currencies, setCurrencies] = useState(["USD"]);
  const [country, setCountry] = useState("US");
  const [billingAddress, setBillingAddress] = useState("");
  const [taxRegistered, setTaxRegistered] = useState(true);
  const [taxNo, setTaxNo] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("unverified");

  useEffect(() => {
    Promise.all([getTaxProfile(), getCurrencyConfig()])
      .then(([tax, currencyConfig]) => {
        if (tax) {
          setBusinessName(tax.billing_name || "");
          setCountry(tax.tax_country || "US");
          setBillingAddress(tax.billing_address || "");
          setTaxNo(tax.tax_id || "");
          setTaxRegistered(Boolean(tax.tax_id));
          setVerificationStatus(tax.tax_verification_status || "unverified");
        }
        if (currencyConfig?.supported_currencies?.length) setCurrencies(currencyConfig.supported_currencies);
        if (currencyConfig?.currency) setCurrency(currencyConfig.currency);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    // client-api's Joi schema treats billing_name/tax_id as optional - enforcing "required" here
    // is a UI-level rule (matches the Business Name field's existing red asterisk), not a backend
    // constraint, so a submission that violates it never reaches the API at all.
    if (!businessName.trim()) {
      toast.error("Business Name is required");
      return;
    }
    if (taxRegistered && !taxNo.trim()) {
      toast.error("Tax No. is required when Tax Registered is Yes");
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        upsertTaxProfile({
          billing_name: businessName || undefined,
          tax_country: country,
          billing_address: billingAddress || undefined,
          tax_id: taxRegistered ? taxNo || undefined : undefined,
          // Explicit signal so the backend actually clears tax_id/tax_id_type/verification
          // status when switching to No, instead of silently leaving the old value in place.
          tax_registered: taxRegistered,
        }),
        updateCurrency({ currency_code: currency }),
      ]);
      toast.success("Tax information saved");
      setVerificationStatus(taxRegistered && taxNo ? "pending" : "unverified");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save tax information, try again");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 p-6 pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading tax information...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Tax &amp; Billing Information</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Provide your Business and Tax details for invoicing and Compliance
                </p>
                <div className="border-b border-gray-200 mt-2 w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Business Name<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Enter your Billing Business Name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
            />

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {currencies.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="US">United States</option>
                  <option value="IN">India</option>
                  <option value="GB">United Kingdom</option>
                </select>
              </div>
            </div>

            <label className="block text-sm font-semibold text-gray-700 mb-1">Billing Address</label>
            <textarea
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              placeholder="Enter your Billing Address"
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
            />

            <div className="flex items-center gap-4 mb-3">
              <span className="text-sm font-semibold text-gray-700">Tax Registered:</span>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" checked={taxRegistered} onChange={() => setTaxRegistered(true)} />
                Yes
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input type="radio" checked={!taxRegistered} onChange={() => setTaxRegistered(false)} />
                No
              </label>
            </div>

            {taxRegistered && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Tax No. {/* Label/format (GSTIN vs VAT) depends on country - India -> GSTIN, elsewhere -> VAT */}
                </label>
                <div className="flex items-center gap-2 max-w-md">
                  <input
                    type="text"
                    value={taxNo}
                    onChange={(e) => setTaxNo(e.target.value)}
                    placeholder="Enter your Tax No"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <span
                    className={`flex items-center gap-1 text-xs rounded-full px-3 py-1.5 whitespace-nowrap ${
                      verificationStatus === "verified"
                        ? "bg-gradient-to-r from-[#08aeea] to-[#39d98a] text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {verificationStatus === "verified" && (
                      <img src={checkCircleIcon} alt="" className="w-4 h-4" />
                    )}
                    {verificationStatus === "verified" ? "Tax Verified" : "Pending Verification"}
                  </span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#095228] text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Tax Information"}
            </button>
          </div>
        </main>
      </main>
    </div>
  );
}
